import { useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import * as Toast from '@radix-ui/react-toast'
import { Cross2Icon } from '@radix-ui/react-icons'
import { useTranslation } from 'react-i18next'
import { useBuySellStore } from '@/reducers/transactionReducer'
import {
  stableCoinLogos,
  StableCoins,
  NetworkValue,
  StableCoinValue,
  Networks,
} from '@/utils/currencies'
import { useSupportedCurrencies } from '@/hooks/useSupportedCurrencies'
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { Tokens } from '../Select/TokenPicker/Tokens'
import { NetworkItems } from '../Select/NetworkItems'
import { track } from '@/utils/analytics'

export const WidgetModal = ({
  disableNetworkSelect,
  fullWidth = false,
}: {
  disableNetworkSelect: boolean
  fullWidth?: boolean
}) => {
  const { t } = useTranslation()
  const { enableCadcSolana } = useFeatureFlags()
  const supportedCurrencies = useSupportedCurrencies()
  const stableCoin = useBuySellStore.use.stableCoin()
  const setStableCoin = useBuySellStore.use.setStableCoin()
  const network = useBuySellStore.use.network()
  const setNetwork = useBuySellStore.use.setNetwork()
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [dialogPage1, setDialogPage1] = useState(true)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const handleOpenChange = (open: boolean) => {
    if (disableNetworkSelect) {
      return
    }
    setIsDialogOpen(open)
  }
  const DEFAULT_NETWORK = Networks.ethereum
  const handleNetworkSelect = (selectedNetwork: NetworkValue): void => {
    track('network_selected', {
      network: selectedNetwork,
      previous_network: network,
      coin: stableCoin,
    })
    // Check if current stableCoin is supported on selected network
    if (!supportedCurrencies[selectedNetwork].includes(stableCoin)) {
      // If the selected network doesn't support the current token,
      // check if there's a network that does support it
      const availableNetworks = Object.entries(supportedCurrencies)
        .filter(([_, tokens]) => tokens.includes(stableCoin))
        .map(([network]) => network as NetworkValue)
      if (availableNetworks.length > 0) {
        // There are networks that support this token
        setNetwork(selectedNetwork, enableCadcSolana)
        setShowToast(true)
        setToastMessage(
          t('widgetModal.toast.networkDoesntSupport', {
            network: selectedNetwork,
            token: stableCoin,
          })
        )
      } else {
        // No networks support this token, fall back to default network and USDC
        setNetwork(DEFAULT_NETWORK, enableCadcSolana)
        const defaultToken: StableCoinValue = StableCoins.USDC
        setStableCoin(defaultToken)
        setShowToast(true)
        setToastMessage(
          t('widgetModal.toast.tokenNotSupportedFallback', {
            token: stableCoin,
            network: selectedNetwork,
            defaultToken: defaultToken,
            defaultNetwork: DEFAULT_NETWORK,
          })
        )
      }
    } else {
      setNetwork(selectedNetwork, enableCadcSolana)
    }
    setDialogPage1(true)
    setIsDialogOpen(false)
  }
  const handleTokenSelect = (selectedToken: StableCoinValue): void => {
    track('stable_coin_selected', {
      coin: selectedToken,
      previous_coin: stableCoin,
      network,
    })
    if (!supportedCurrencies[network].includes(selectedToken)) {
      // Find networks that support this token
      const availableNetworks = Object.entries(supportedCurrencies)
        .filter(([_, tokens]) => tokens.includes(selectedToken))
        .map(([network]) => network as NetworkValue)
      if (availableNetworks.length > 0) {
        // Switch to the first available network that supports this token
        const newNetwork = availableNetworks[0]
        setNetwork(newNetwork, enableCadcSolana)
        setStableCoin(selectedToken)
        setShowToast(true)
        setToastMessage(
          t('widgetModal.toast.tokenNotSupportedSwitching', {
            token: selectedToken,
            currentNetwork: network,
            newNetwork: newNetwork,
          })
        )
      } else {
        // If no networks support this token, keep current network and show error
        setShowToast(true)
        setToastMessage(
          t('widgetModal.toast.tokenNotSupportedAny', {
            token: selectedToken,
          })
        )
        return
      }
    } else {
      setStableCoin(selectedToken)
    }
    setDialogPage1(false)
  }
  return (
    <Toast.Provider swipeDirection="right" duration={3000}>
      <Dialog.Root open={isDialogOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          <div
            className={`${
              fullWidth
                ? 'w-full flex items-center justify-between pb-1'
                : 'flex-shrink min-w-min flex items-end mb-2'
            } ${disableNetworkSelect ? 'cursor-default' : 'cursor-pointer'}`}
            data-testid="modal-modal"
          >
            <span
              style={{
                pointerEvents: 'none',
              }}
            >
              <div className="flex">
                <div className="mr-1 mt-1 w-15">
                  <img
                    src={stableCoinLogos[`${stableCoin}${network}`]}
                    height={40}
                    width={40}
                    alt={`${stableCoin} on ${network}`}
                    data-testid="modal-modal"
                  />
                </div>
                <div className="text-left flex flex-col justify-center">
                  <div
                    className="flex row font-normal"
                    data-testid="modal-modal"
                  >
                    {stableCoin}
                  </div>
                  <div
                    className="text-sm text-pebble dark:text-white"
                    data-testid="modal-modal"
                  >
                    {network}
                  </div>
                </div>
              </div>
            </span>
            {!disableNetworkSelect && (
              <span
                style={{
                  marginLeft: '8px',
                }}
              >
                <svg
                  style={{
                    marginBottom: fullWidth ? '0' : '20px',
                  }}
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  data-testid="modal-modal"
                >
                  <path
                    d="M3.13523 6.15803C3.3241 5.95657 3.64052 5.94637 3.84197 6.13523L7.5 9.56464L11.158 6.13523C11.3595 5.94637 11.6759 5.95657 11.8648 6.15803C12.0536 6.35949 12.0434 6.67591 11.842 6.86477L7.84197 10.6148C7.64964 10.7951 7.35036 10.7951 7.15803 10.6148L3.15803 6.86477C2.95657 6.67591 2.94637 6.35949 3.13523 6.15803Z"
                    fill="currentColor"
                    fillRule="evenodd"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </span>
            )}
          </div>
        </Dialog.Trigger>

        <Dialog.Portal>
          <Dialog.Overlay
            className="fixed inset-0 bg-black opacity-60 z-40"
            data-testid="modal-modal"
          />
          <Dialog.Content
            className="DialogContent bg-white dark:bg-pebble z-50"
            onOpenAutoFocus={(e) => e.preventDefault()}
            style={{
              top: dialogPage1 ? '250px' : '50px',
              transform: 'translateX(-50%)',
              overflow: 'auto',
            }}
            data-testid="modal-modal"
          >
            {dialogPage1 && (
              <div data-testid="modal-modal">
                <Dialog.Title className="DialogTitle text-black dark:text-white">
                  <h3
                    style={{
                      textAlign: 'center',
                    }}
                    data-testid="modal-modal"
                  >
                    <b>{t('widgetModal.selectStablecoin')}</b>
                  </h3>
                </Dialog.Title>
                <Tokens onSelectToken={handleTokenSelect} />
              </div>
            )}

            {!dialogPage1 && (
              <div data-testid="modal-modal">
                <NetworkItems onSelectNetwork={handleNetworkSelect} />
              </div>
            )}

            <Dialog.Close asChild>
              <button
                style={{
                  position: 'absolute',
                  top: '10px',
                  left: '10px',
                }}
                aria-label="Close"
                data-testid="modal-modal-close"
              >
                <Cross2Icon />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Toast.Root
        className="bg-white dark:bg-slate-800 rounded-md shadow-lg p-4 flex items-center gap-2 fixed bottom-4 right-4"
        open={showToast}
        onOpenChange={setShowToast}
        data-testid="modal-modal"
      >
        <Toast.Title
          className="text-sm font-medium text-slate-900 dark:text-white"
          data-testid="modal-modal"
        >
          {toastMessage}
        </Toast.Title>
      </Toast.Root>

      <Toast.Viewport className="fixed bottom-0 right-0 p-6 m-0 flex flex-col gap-2 w-96 list-none z-50" />
    </Toast.Provider>
  )
}

/* 
POTENTIAL IMPROVEMENTS:
1. Accessibility: The modal could benefit from improved keyboard navigation and screen reader support.
2. Error handling: Network/token compatibility messages could be more informative.
3. UX: Consider adding loading states during network/token transitions.
4. Mobile UX: The dialog positioning might need adjustments for smaller screens.
5. Performance: Consider memoizing some of the components to prevent unnecessary re-renders.
6. Search functionality: Adding search capability for tokens and networks would improve UX.
7. Visual feedback: Add visual indicators for unsupported token/network combinations.
*/
